import { Injectable, Renderer2 } from "@angular/core";
import { DeviceInfo } from "@capacitor/device";
import { ModalController, Platform } from "@ionic/angular";
import { Store } from "@ngxs/store";

import {
  BehaviorSubject,
  combineLatest,
  from,
  Observable,
  ReplaySubject,
  Subject,
} from "rxjs";
import { filter, map, switchMap } from "rxjs/operators";
import UAParser from "ua-parser-js";

import * as EventTypes from "../../../assets/json/events.json";
import { ApplicationGetterState } from "../../ngxs/application";
import { SetDeviceInfoAction } from "../../ngxs/application/application.actions";

@Injectable({
  providedIn: "root",
})
export class ApplicationService {
  deviceInfo$: Observable<DeviceInfo> = this.store.select(
    ApplicationGetterState.getDeviceInfo
  );
  isRealMobileDevice$: Observable<boolean> = this.store.select(
    ApplicationGetterState.getIsRealMobileDevice
  );

  renderer2: Renderer2;
  platformParser = new UAParser();
  defaultTimeZone = "Australia/Perth";
  eventTypes: { EventID: string; Description: string }[] = (EventTypes as any)
    .default;
  minimizeModalEvent$: Subject<string> = new Subject();
  isMapLoading$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  shownAppSpinner$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  isShownAppSpinner$: Observable<boolean>;

  isMenuOpened$: ReplaySubject<boolean> = new ReplaySubject(1);

  private isMenuOpenedNow = false;
  private _isMobile: boolean;
  private _isDesktop: boolean;

  get isMobile(): boolean {
    return this._isMobile;
  }

  get isDesktop(): boolean {
    return this._isDesktop;
  }

  constructor(
    private store: Store,
    private platform: Platform,
    private modalController: ModalController
  ) {
    this.platform.ready().then(() => {
      this._isMobile = this.platform.is("mobile") && !this.isWindowsPlatform;
      this._isDesktop = this.platform.is("desktop") || this.isWindowsPlatform;
    });

    this.isShownAppSpinner$ = combineLatest([
      this.isMapLoading$,
      this.shownAppSpinner$,
    ]).pipe(
      map(([isMapLoading, shownAppSpinner]) => {
        return isMapLoading || shownAppSpinner;
      })
    );

    this.isMenuOpened$.subscribe(
      (isMenuOpened) => (this.isMenuOpenedNow = isMenuOpened)
    );

    this.isMenuOpened$
      .pipe(
        filter((isMenuOpened) => !!isMenuOpened),
        switchMap(() => from(this.modalController.getTop())),
        filter((isModalOpened) => !!isModalOpened)
      )
      .subscribe(() => {
        this.modalController.dismiss();
      });
  }

  /**
   * Padding for fitting bounds on the map pages. Their canvas is a static
   * full-viewport layer that the open side menu overlays, so fits reserve the
   * menu's 175px on the left while it is open. 45 mirrors fitBounds' own
   * default padding, keeping the closed-state framing unchanged.
   */
  getMapFitPadding(): number | google.maps.Padding {
    if (this.isMenuOpenedNow && this.isDesktop) {
      return { left: 220, top: 45, right: 45, bottom: 45 };
    }
    return 45;
  }

  setDeviceInfo() {
    this.store.dispatch(new SetDeviceInfoAction());
  }

  initRenderer(renderer: Renderer2) {
    this.renderer2 = renderer;
  }

  minimizeCurrentModal(data: string) {
    this.minimizeModalEvent$.next(data);
  }

  mapFitBounds(
    mapInstance: google.maps.Map,
    positions: google.maps.LatLngLiteral[]
  ) {
    const bounds = new google.maps.LatLngBounds();
    positions.forEach((position) => bounds.extend(position));

    if (bounds.isEmpty()) {
      return;
    }

    // Don't zoom in too far on only one marker
    if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
      const northEastLat = bounds.getNorthEast().lat();
      const northEastLng = bounds.getNorthEast().lng();
      const extendPoint1 = new google.maps.LatLng(
        northEastLat + 0.002,
        northEastLng + 0.002
      );
      const extendPoint2 = new google.maps.LatLng(
        northEastLat - 0.002,
        northEastLng - 0.002
      );
      bounds.extend(extendPoint1);
      bounds.extend(extendPoint2);
    }

    mapInstance.fitBounds(bounds, this.getMapFitPadding());
  }

  private get isWindowsPlatform(): boolean {
    return (
      this.platformParser.getOS()?.name?.indexOf("Windows") >= 0 ||
      this.platformParser.getUA()?.indexOf("Windows") >= 0
    );
  }
}
